import React from "react";
import { Button } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import useTheme from "../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../providers/ThemeProvider";
import RoundedIconContainer from "../../common/RoundedIconContainer";
import {
  DEFAULT_LANE_ORDER,
  moveLane,
  SERVICE_LANES,
  ServiceLaneKey,
} from "./serviceLanes";

type Props = {
  /** Every lane key, in the order the lanes are drawn. */
  order: ServiceLaneKey[];
  /** Services in the current period per lane; a lane missing here has none. */
  counts: Partial<Record<ServiceLaneKey, number>>;
  onChange: (order: ServiceLaneKey[]) => void;
};

const DRAG_TYPE = "TIMELINE_LANE";

/**
 * The toolbar's "Services" card: the five service lanes in their current
 * order, rearranged by dragging a row's handle or with its up / down arrows.
 * Every lane is listed, with its count of services in the period, so a lane
 * that is empty right now keeps its place for when it has services again.
 *
 * Styled like the Target picker (dashboard/ChartTargetPicker): a 300px card
 * with a title row and a text button on the right.
 */
const ServiceLanePicker = ({ order, counts, onChange }: Props) => {
  const theme: Theme = useTheme();
  const isDefault = order.every((key, index) => key === DEFAULT_LANE_ORDER[index]);

  return (
    <div style={styles.container(theme)}>
      <div style={styles.header(theme)}>
        <div style={styles.titleBlock}>
          <div style={styles.title(theme)}>Services order</div>
          <div style={styles.subtitle(theme)}>
            Drag a lane, or use the arrows, to change the order.
          </div>
        </div>
        <Button
          sx={styles.resetButton(theme)}
          onClick={() => onChange(DEFAULT_LANE_ORDER)}
          disabled={isDefault}
        >
          Reset
        </Button>
      </div>
      <DndProvider backend={HTML5Backend}>
        <div style={styles.list}>
          {order.map((key, index) => (
            <LaneRow
              key={key}
              laneKey={key}
              index={index}
              count={counts[key] ?? 0}
              last={index === order.length - 1}
              onMove={(from, to) => onChange(moveLane(order, from, to))}
              indexOf={(k) => order.indexOf(k)}
            />
          ))}
        </div>
      </DndProvider>
    </div>
  );
};

export default ServiceLanePicker;

const LaneRow = ({
  laneKey,
  index,
  count,
  last,
  onMove,
  indexOf,
}: {
  laneKey: ServiceLaneKey;
  index: number;
  count: number;
  last: boolean;
  onMove: (from: number, to: number) => void;
  indexOf: (key: ServiceLaneKey) => number;
}) => {
  const theme: Theme = useTheme();
  const label = SERVICE_LANES.find((lane) => lane.key === laneKey)?.label ?? laneKey;

  const [{ isDragging }, dragRef] = useDrag({
    type: DRAG_TYPE,
    item: { key: laneKey },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  // Reorder while hovering, so the list rearranges live under the pointer.
  const [, dropRef] = useDrop<{ key: ServiceLaneKey }>({
    accept: DRAG_TYPE,
    hover: (item) => {
      if (item.key === laneKey) return;
      const from = indexOf(item.key);
      const to = indexOf(laneKey);
      if (from !== -1 && to !== -1) onMove(from, to);
    },
  });

  return (
    <div ref={dropRef} style={styles.row(theme, isDragging)}>
      <div ref={dragRef} style={styles.handle(theme)} aria-label={`Drag ${label}`}>
        <DragIndicatorIcon style={styles.handleIcon(theme)} />
      </div>
      <div style={styles.label(theme)}>
        <span style={styles.position(theme)}>{index + 1}.</span>
        {label}
      </div>
      <div style={styles.count(theme, count === 0)}>{count}</div>
      <div style={styles.arrows}>
        <div style={styles.arrow(index === 0)} aria-label={`Move ${label} up`}>
          <RoundedIconContainer
            handleClick={() => onMove(index, index - 1)}
            isDisabled={index === 0}
          >
            <KeyboardArrowUpIcon style={styles.arrowIcon(theme)} />
          </RoundedIconContainer>
        </div>
        <div style={styles.arrow(last)} aria-label={`Move ${label} down`}>
          <RoundedIconContainer
            handleClick={() => onMove(index, index + 1)}
            isDisabled={last}
          >
            <KeyboardArrowDownIcon style={styles.arrowIcon(theme)} />
          </RoundedIconContainer>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: (theme: Theme) => ({
    width: "320px",
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    padding: "16px",
    borderRadius: "8px",
  }),
  header: (theme: Theme) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    paddingBottom: "8px",
    marginBottom: "8px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  titleBlock: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "2px",
    minWidth: 0,
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  subtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  resetButton: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    textTransform: "none" as "none",
    padding: "0",
    minWidth: 0,
    flexShrink: 0,
    "&:hover": { backgroundColor: "transparent" },
  }),
  list: {
    display: "flex",
    flexDirection: "column" as "column",
  },
  row: (theme: Theme, dragging: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "4px 4px 4px 0",
    borderRadius: "4px",
    // The dashboard's cards fade the same way while they are dragged.
    opacity: dragging ? 0.3 : 1,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  handle: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    cursor: "grab",
    color: theme.colors_ds.NEUTRAL_ICON_DEFAULT,
  }),
  handleIcon: (theme: Theme) => ({
    fontSize: "18px",
    color: theme.colors_ds.NEUTRAL_ICON_DEFAULT,
  }),
  label: (theme: Theme) => ({
    flex: 1,
    minWidth: 0,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  // The lane's place in the order, so a change reads at a glance.
  position: (theme: Theme) => ({
    display: "inline-block",
    minWidth: "18px",
    fontFamily: "var(--font-Regular)",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  count: (theme: Theme, empty: boolean) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    opacity: empty ? 0.5 : 1,
    minWidth: "20px",
    textAlign: "right" as "right",
  }),
  arrows: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
  },
  arrow: (disabled: boolean) => ({
    opacity: disabled ? 0.3 : 1,
  }),
  // The same chevron as the toolbar's dropdowns, so the pair match.
  arrowIcon: (theme: Theme) => ({
    fontSize: "18px",
    color: theme.colors_ds.NEUTRAL_ICON_DEFAULT,
    display: "block",
  }),
};
